/**
 * 國際化路由 Composable
 * 幫助組件生成帶語言前綴的路徑
 */

import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { 
  getLocaleFromPath, 
  addLocaleToPath,
  i18nToLocaleMap
} from '../router/utils'

export function useI18nRouter() {
  const route = useRoute()
  const router = useRouter()

  // 獲取當前路由的語言代碼
  const currentLocale = computed(() => {
    return getLocaleFromPath(route.path) || 'en'
  })

  /**
   * 生成帶語言前綴的路徑
   * @param path - 路徑（不包含語言前綴）
   * @param locale - 可選，指定語言代碼，默認為當前語言
   * @returns 帶語言前綴的完整路徑
   */
  const localePath = (path: string, locale?: string) => {
    const targetLocale = locale || currentLocale.value
    return addLocaleToPath(path, targetLocale)
  }

  /**
   * 導航到指定路徑（帶語言前綴）
   * @param path - 路徑（不包含語言前綴）
   * @param locale - 可選，指定語言代碼，默認為當前語言
   */
  const localePush = (path: string, locale?: string) => {
    const targetPath = localePath(path, locale)
    return router.push(targetPath)
  }

  /**
   * 切換語言並導航到當前頁面的對應語言版本
   * @param i18nLocale - i18n 語言代碼（如 'en-US', 'zh-TW', 'zh-CN', 'ja-JP'）
   */
  const switchLocale = (i18nLocale: string) => {
    // 獲取 URL 語言代碼
    const urlLocale = i18nToLocaleMap[i18nLocale] || 'en'
    
    // 從當前路徑移除語言前綴，獲取基礎路徑
    const basePath = route.path.replace(/^\/(en|zh|ja)/, '') || '/'
    
    // 生成新路徑
    const newPath = localePath(basePath, urlLocale)
    
    // 保留查詢參數和 hash
    return router.push({
      path: newPath,
      query: route.query,
      hash: route.hash
    })
  }

  /**
   * 根據路由名稱和當前語言生成完整的路由名稱
   * @param routeName - 基礎路由名稱（如 'Home', 'Invest'）
   * @param locale - 可選，指定語言代碼，默認為當前語言
   * @returns 完整的路由名稱（如 'Home_en', 'Invest_zh'）
   */
  const localeRouteName = (routeName: string, locale?: string) => {
    const targetLocale = locale || currentLocale.value
    return `${routeName}_${targetLocale}`
  }

  /**
   * 使用路由名稱導航（自動添加語言後綴）
   * @param routeName - 基礎路由名稱（如 'Home', 'Invest'）
   * @param options - 路由選項（query, params 等）
   */
  const localePushByName = (routeName: string, options: any = {}) => {
    const fullRouteName = localeRouteName(routeName)
    return router.push({
      name: fullRouteName,
      ...options
    })
  }

  return {
    currentLocale,
    localePath,
    localePush,
    switchLocale,
    localeRouteName,
    localePushByName
  }
}

